import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from 'react'
import styles from './DescriptionAutofill.module.css'
import apiClient from '../utils/api'
import ProjectTaskMentions from './ProjectTaskMentions'
import { sanitizeUserMentions } from '../utils/textUtils'

const DescriptionAutofill = forwardRef(({
    value,
    onChange,
    onSuggestionSelect,
    onKeyDown,
    placeholder = "What are you working on?",
    className = "",
    apiToken = null,
    userId = null,
    projects = [],
    tasks = [],
    onProjectTaskMention = null,
    teammates = [],
    selectedProject = null
}, ref) => {
    const [suggestions, setSuggestions] = useState([])
    const [filteredSuggestions, setFilteredSuggestions] = useState([])
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [loading, setLoading] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(-1)
    const inputRef = useRef(null)
    const suggestionsRef = useRef(null)

    // State for # mention autocomplete
    const [showMentions, setShowMentions] = useState(false)
    const [mentionType, setMentionType] = useState('project') // 'project' or 'task'
    const [mentionQuery, setMentionQuery] = useState('')
    const [mentionSuggestions, setMentionSuggestions] = useState([])
    const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0)
    const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 })
    const [mentionStartIndex, setMentionStartIndex] = useState(-1)
    const [activeMentionSymbol, setActiveMentionSymbol] = useState('#')

    const allowedUsernames = useMemo(() => {
        if (!Array.isArray(teammates)) return []
        const set = new Set()
        teammates.forEach(user => {
            if (user?.username) {
                set.add(user.username)
            }
        })
        return Array.from(set)
    }, [teammates])

    // Expose focus method to parent component
    useImperativeHandle(ref, () => ({
        focus: () => {
            if (inputRef.current) {
                inputRef.current.focus()
            }
        }
    }))

    // Load suggestions when component mounts
    useEffect(() => {
        if (apiToken) {
            loadDescriptionSuggestions()
        }
    }, [apiToken, userId])

    const loadDescriptionSuggestions = async () => {
        try {
            setLoading(true)
            apiClient.setAuthToken(apiToken)
            const response = await apiClient.getDescriptionSuggestions(userId)
            setSuggestions(response.suggestions || [])
        } catch (error) {
            console.error('Failed to load description suggestions:', error)
            setSuggestions([])
        } finally {
            setLoading(false)
        }
    }

    const isWordCharacter = (char) => /[a-zA-Z0-9_\-\.]/.test(char)

    const detectSymbolMention = (text, cursorPosition, symbol) => {
        if (!text) return null

        let symbolIndex = -1
        for (let i = cursorPosition - 1; i >= 0; i--) {
            const char = text[i]
            if (char === symbol) {
                // Prevent triggering inside emails/words for @ symbols
                if (symbol === '@' && i > 0 && isWordCharacter(text[i - 1])) {
                    return null
                }
                symbolIndex = i
                break
            }
            if (/\s/.test(char) && i < cursorPosition - 1) {
                break
            }
        }

        if (symbolIndex === -1) return null

        const queryText = text.slice(symbolIndex + 1, cursorPosition)
        if (/\s/.test(queryText)) return null

        return {
            startIndex: symbolIndex,
            query: queryText,
            symbol
        }
    }

    // Helper to find matching item (project or task) by name
    const findMatchingItem = (pattern, items) => {
        if (!pattern || !items || items.length === 0) return null

        const lowerPattern = pattern.toLowerCase()

        // Try exact match first (case-insensitive)
        const exactMatch = items.find(item =>
            item.name.toLowerCase() === lowerPattern
        )
        if (exactMatch) return exactMatch

        // Try starts-with match
        const startsWithMatch = items.find(item =>
            item.name.toLowerCase().startsWith(lowerPattern)
        )
        if (startsWithMatch) return startsWithMatch

        // Try contains match
        const containsMatch = items.find(item =>
            item.name.toLowerCase().includes(lowerPattern)
        )

        return containsMatch
    }

    // Filter project/task suggestions based on mention query
    const filterMentionSuggestions = (query, type, customItems = null) => {
        const lowerQuery = query.toLowerCase()

        if (type === 'user') {
            const items = customItems || teammates || []

            if (!query) {
                return items // Show all users when no query
            }

            return items.filter(user => {
                const displayName = user.display_name || user.full_name || user.first_name || ''
                const username = user.username || ''
                const email = user.email || ''

                return (
                    displayName.toLowerCase().includes(lowerQuery) ||
                    username.toLowerCase().includes(lowerQuery) ||
                    email.toLowerCase().includes(lowerQuery)
                )
            }).sort((a, b) => {
                const aHandle = (a.username || '').toLowerCase()
                const bHandle = (b.username || '').toLowerCase()
                const aStarts = aHandle.startsWith(lowerQuery)
                const bStarts = bHandle.startsWith(lowerQuery)
                if (aStarts && !bStarts) return -1
                if (!aStarts && bStarts) return 1
                return (a.display_name || aHandle).length - (b.display_name || bHandle).length
            }) // Show all matching users
        }

        let items = customItems || (type === 'project' ? projects : tasks)

        if (!query) {
            return items.slice(0, 10) // Show top 10 if no query
        }

        return items.filter(item =>
            item.name.toLowerCase().includes(lowerQuery)
        ).sort((a, b) => {
            // Prioritize starts-with matches
            const aStarts = a.name.toLowerCase().startsWith(lowerQuery)
            const bStarts = b.name.toLowerCase().startsWith(lowerQuery)

            if (aStarts && !bStarts) return -1
            if (!aStarts && bStarts) return 1

            return a.name.length - b.name.length
        }).slice(0, 10)
    }

    // Update mention suggestions when input changes
    useEffect(() => {
        if (!inputRef.current) return

        const cursorPosition = inputRef.current.selectionStart
        const hashMention = detectSymbolMention(value, cursorPosition, '#')
        const atMention = detectSymbolMention(value, cursorPosition, '@')

        let activeMention = null
        if (hashMention && atMention) {
            activeMention = hashMention.startIndex > atMention.startIndex ? hashMention : atMention
        } else {
            activeMention = hashMention || atMention
        }

        if (activeMention) {
            if (activeMention.symbol === '#') {
                // Determine if we should show projects or tasks based on selectedProject prop
                let type = 'project'
                let itemsToFilter = projects

                // If a project is already selected, show tasks for that project
                if (selectedProject) {
                    type = 'task'
                    itemsToFilter = tasks.filter(t => t.project_id === selectedProject.id)
                }

                const filtered = filterMentionSuggestions(activeMention.query, type, itemsToFilter)
                setMentionType(type)
                setMentionQuery(activeMention.query)
                setMentionSuggestions(filtered)
                setMentionStartIndex(activeMention.startIndex)
                setMentionSelectedIndex(0)
                setActiveMentionSymbol('#')

                const input = inputRef.current
                const rect = input.getBoundingClientRect()
                setMentionPosition({
                    top: rect.bottom + window.scrollY,
                    left: rect.left + window.scrollX
                })

                setShowMentions(filtered.length > 0)
                setShowSuggestions(false)
                return
            }

            if (activeMention.symbol === '@') {
                const filtered = filterMentionSuggestions(activeMention.query, 'user', teammates)
                setMentionType('user')
                setMentionQuery(activeMention.query)
                setMentionSuggestions(filtered)
                setMentionStartIndex(activeMention.startIndex)
                setMentionSelectedIndex(0)
                setActiveMentionSymbol('@')

                const input = inputRef.current
                const rect = input.getBoundingClientRect()
                setMentionPosition({
                    top: rect.bottom + window.scrollY,
                    left: rect.left + window.scrollX
                })

                // Always show mentions dropdown when @ is detected (even if no matches)
                // This allows users to type @username manually without selecting from dropdown
                setShowMentions(true)
                setShowSuggestions(false)
                return
            }
        }

        setShowMentions(false)
    }, [value, projects, tasks, teammates, selectedProject])

    // Filter suggestions based on input value
    useEffect(() => {
        let filtered = []

        // Only show suggestions if there's input (at least 1 character)
        if (value && value.length > 0) {
            // Filter based on input, prioritize starts-with matches
            const query = value.toLowerCase()
            filtered = suggestions.filter(suggestion =>
                suggestion.description.toLowerCase().includes(query)
            ).sort((a, b) => {
                // Prioritize matches that start with the query
                const aStarts = a.description.toLowerCase().startsWith(query)
                const bStarts = b.description.toLowerCase().startsWith(query)

                if (aStarts && !bStarts) return -1
                if (!aStarts && bStarts) return 1

                // Then sort by relevance (shorter descriptions first for exact matches)
                return a.description.length - b.description.length
            })
        }

        // Limit to 8 most relevant suggestions (no scroll needed)
        setFilteredSuggestions(filtered.slice(0, 8))
        setSelectedIndex(-1)
    }, [value, suggestions])

    const handleInputChange = (e) => {
        const newValue = e.target.value
        // Don't sanitize while typing - let users freely type @mentions
        // Sanitization should only happen on submission, not during input
        onChange(newValue)
        // Only show suggestions if user has typed something
        if (newValue && newValue.length > 0) {
            setShowSuggestions(true)
        } else {
            setShowSuggestions(false)
        }
    }

    const handleInputFocus = () => {
        // Only show suggestions if there's already text in the input
        if (value && value.length > 0) {
            setShowSuggestions(true)
        }
    }

    const handleInputBlur = () => {
        // Delay hiding suggestions to allow clicking on them
        setTimeout(() => {
            setShowSuggestions(false)
            setShowMentions(false)
        }, 150)
    }

    const handleSuggestionClick = (suggestion) => {
        onChange(suggestion.description)
        setShowSuggestions(false)
        
        // Call the callback with the full suggestion data
        if (onSuggestionSelect) {
            onSuggestionSelect(suggestion)
        }
    }

    // Handle mention selection
    const handleMentionSelect = (item) => {
        if (mentionStartIndex === -1) return

        const before = value.slice(0, mentionStartIndex)
        const after = value.slice(inputRef.current.selectionStart)

        // For #project and #task mentions, remove them from description
        if (mentionType !== 'user') {
            // Remove the # mention and normalize spacing
            const beforeTrimmed = before.trimEnd()
            const afterTrimmed = after.trimStart()

            // Add single space between if there's content after
            const newValue = afterTrimmed
                ? `${beforeTrimmed} ${afterTrimmed}`
                : beforeTrimmed

            onChange(newValue)
            setShowMentions(false)

            // Notify parent to set project/task
            if (onProjectTaskMention) {
                onProjectTaskMention({
                    type: mentionType,
                    item: item
                })
            }

            // Position cursor at the end of the before part
            setTimeout(() => {
                if (inputRef.current) {
                    const cursorPos = beforeTrimmed.length
                    inputRef.current.setSelectionRange(cursorPos, cursorPos)
                    inputRef.current.focus()
                }
            }, 0)
        } else {
            // For @user mentions, keep the current behavior
            const mentionValue = item.username || item.email?.split('@')[0] || item.display_name || item.name
            const symbol = '@'
            const newValue = `${before}${symbol}${mentionValue} ${after}`
            onChange(newValue)
            setShowMentions(false)

            setTimeout(() => {
                if (inputRef.current) {
                    const cursorPos = mentionStartIndex + mentionValue.length + 2
                    inputRef.current.setSelectionRange(cursorPos, cursorPos)
                    inputRef.current.focus()
                }
            }, 0)
        }
    }

    const handleKeyDown = (e) => {
        // Handle mention navigation if mentions are shown
        if (showMentions && mentionSuggestions.length > 0) {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault()
                    setMentionSelectedIndex(prev =>
                        prev < mentionSuggestions.length - 1 ? prev + 1 : 0
                    )
                    return
                case 'ArrowUp':
                    e.preventDefault()
                    setMentionSelectedIndex(prev =>
                        prev > 0 ? prev - 1 : mentionSuggestions.length - 1
                    )
                    return
                case 'Enter':
                case 'Tab':
                    e.preventDefault()
                    if (mentionSelectedIndex >= 0 && mentionSelectedIndex < mentionSuggestions.length) {
                        handleMentionSelect(mentionSuggestions[mentionSelectedIndex])
                    }
                    return
                case 'Escape':
                    e.preventDefault()
                    setShowMentions(false)
                    return
            }
        }

        // Handle suggestions navigation only if suggestions are shown
        if (showSuggestions && filteredSuggestions.length > 0) {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault()
                    setSelectedIndex(prev =>
                        prev < filteredSuggestions.length - 1 ? prev + 1 : prev === -1 ? 0 : prev
                    )
                    return
                case 'ArrowUp':
                    e.preventDefault()
                    setSelectedIndex(prev =>
                        prev > 0 ? prev - 1 : filteredSuggestions.length - 1
                    )
                    return
                case 'Enter':
                    if (selectedIndex >= 0 && selectedIndex < filteredSuggestions.length) {
                        e.preventDefault()
                        const suggestion = filteredSuggestions[selectedIndex]
                        handleSuggestionClick(suggestion)
                    }
                    return
                case 'Tab':
                    // Auto-select first suggestion on Tab if available
                    if (filteredSuggestions.length > 0) {
                        e.preventDefault()
                        const suggestionToSelect = selectedIndex >= 0
                            ? filteredSuggestions[selectedIndex]
                            : filteredSuggestions[0]
                        handleSuggestionClick(suggestionToSelect)
                    }
                    return
                case 'Escape':
                    e.preventDefault()
                    setShowSuggestions(false)
                    setSelectedIndex(-1)
                    return
            }
        }

        // Call parent's onKeyDown handler if provided
        if (onKeyDown) {
            onKeyDown(e)
        }
    }

    return (
        <div className={styles.container}>
            <input
                ref={inputRef}
                type="text"
                className={`${styles.input} ${className}`}
                value={value}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
            />
            
            {showSuggestions && filteredSuggestions.length > 0 && (
                <div ref={suggestionsRef} className={styles.suggestions}>
                    {filteredSuggestions.map((suggestion, index) => (
                        <div
                            key={index}
                            className={`${styles.suggestion} ${
                                index === selectedIndex ? styles.selected : ''
                            }`}
                            onClick={() => handleSuggestionClick(suggestion)}
                        >
                            <div className={styles.suggestionContent}>
                                <div className={styles.description}>
                                    {suggestion.description}
                                </div>
                                <div className={styles.metadata}>
                                    {suggestion.project_name && (
                                        <span className={styles.project}>
                                            {suggestion.task_name
                                                ? `${suggestion.project_name} > ${suggestion.task_name}`
                                                : suggestion.project_name
                                            }
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showSuggestions && value && value.length > 0 && filteredSuggestions.length === 0 && suggestions.length > 0 && (
                <div className={styles.noResults}>
                    No matching descriptions found
                </div>
            )}

            {showSuggestions && value && value.length > 0 && suggestions.length === 0 && !loading && (
                <div className={styles.noResults}>
                    No previous descriptions found
                </div>
            )}

            {loading && (
                <div className={styles.loading}>
                    Loading suggestions...
                </div>
            )}

            {showMentions && (
                <ProjectTaskMentions
                    suggestions={mentionSuggestions}
                    selectedIndex={mentionSelectedIndex}
                    onSelect={handleMentionSelect}
                    position={mentionPosition}
                    mentionType={mentionType}
                />
            )}
        </div>
    )
})

export default DescriptionAutofill
